import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router'

import { canReturnToVariantMenu, ReceivingFlowController, useReceivingFlow } from './flow'
import { ReceivingNavigationContext } from './components/ReceivingNavigationContext'
import { AsnSelectionScreen } from './screens/AsnSelectionScreen'
import { ConfirmAsnScreen } from './screens/ConfirmAsnScreen'
import { PackMeasurementScreen } from './screens/PackMeasurementScreen'
import { FreightPalletizationScreen } from './screens/FreightPalletizationScreen'
import { LabelPlanScreen } from './screens/LabelPlanScreen'
import { LabelProcessingScreen } from './screens/LabelProcessingScreen'
import { LineProductScanScreen } from './screens/LineProductScanScreen'
import { LineSkuScreen } from './screens/LineSkuScreen'
import { ProductNarrowingScreen } from './screens/ProductNarrowingScreen'
import { ShipmentReferenceScreen } from './screens/ShipmentReferenceScreen'
import { VariantMenuScreen } from './screens/VariantMenuScreen'
import { OverReceiveScreen } from './screens/OverReceiveScreen'
import { ReceivingLocationScreen } from './screens/ReceivingLocationScreen'
import { ReceiptWorkScreen } from './screens/ReceiptWorkScreen'

interface ReceivingFlowViewProps {
  controller: ReceivingFlowController
  embedded?: boolean
  onBack?: () => void
  onExit?: () => void
}

export const ReceivingFlowView = ({ controller, embedded = false, onBack, onExit }: ReceivingFlowViewProps) => {
  const navigate = useNavigate()
  const returnToInbound = onBack ?? (() => navigate('/rf/inbound/'))
  const exitReceiving = () => {
    controller.dispatch({ type: 'reset' })
    if (onExit) onExit()
    else navigate('/rf/')
  }

  useEffect(() => {
    document.title = 'Receiving | Kase'
  }, [])
  useEffect(() => {
    if (controller.state.step === 'receipt-complete') {
      controller.dispatch({ type: 'reset' })
      if (onBack) onBack()
      else navigate('/rf/inbound/')
    }
  }, [controller.state.step, controller.dispatch, navigate, onBack])

  return (
    <ReceivingNavigationContext.Provider
      value={
        canReturnToVariantMenu(controller.state) ? () => controller.dispatch({ type: 'return-variant-menu' }) : null
      }
    >
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#4B0092" />
      </Helmet>
      {controller.state.step === 'scan-shipment-reference' ? (
        <ShipmentReferenceScreen
          controller={controller}
          embedded={embedded}
          onBack={returnToInbound}
          onExit={exitReceiving}
        />
      ) : null}
      {controller.state.step === 'scan-product-to-narrow' ? (
        <ProductNarrowingScreen controller={controller} embedded={embedded} onExit={exitReceiving} />
      ) : null}
      {controller.state.step === 'select-asn' ? (
        <AsnSelectionScreen controller={controller} embedded={embedded} onExit={exitReceiving} />
      ) : null}
      {controller.state.step === 'confirm-asn' ? (
        <ConfirmAsnScreen controller={controller} embedded={embedded} onExit={exitReceiving} />
      ) : null}
      {controller.state.step === 'scan-line-product' ? (
        <LineProductScanScreen controller={controller} embedded={embedded} onExit={exitReceiving} />
      ) : null}
      {controller.state.step === 'enter-line-sku' ? (
        <LineSkuScreen controller={controller} embedded={embedded} onExit={exitReceiving} />
      ) : null}
      {controller.state.step === 'variant-menu' ? (
        <VariantMenuScreen controller={controller} embedded={embedded} onExit={exitReceiving} />
      ) : null}
      {controller.state.step === 'over-receive' ? (
        <OverReceiveScreen controller={controller} embedded={embedded} onExit={exitReceiving} />
      ) : null}
      {controller.state.step === 'measure-pack' ? (
        <PackMeasurementScreen controller={controller} embedded={embedded} onExit={exitReceiving} />
      ) : null}
      {controller.state.step === 'confirm-freight-palletization' ? (
        <FreightPalletizationScreen controller={controller} embedded={embedded} onExit={exitReceiving} />
      ) : null}
      {controller.state.step === 'review-label-plan' ? (
        <LabelPlanScreen controller={controller} embedded={embedded} onExit={exitReceiving} />
      ) : null}
      {controller.state.step === 'label-processing' ? (
        <LabelProcessingScreen controller={controller} embedded={embedded} onExit={exitReceiving} />
      ) : null}
      {controller.state.step === 'receiving-location' ? (
        <ReceivingLocationScreen controller={controller} embedded={embedded} onExit={exitReceiving} />
      ) : null}
      {[
        'scan-pallet-lpn',
        'build-pallet',
        'receive-loose-eaches',
        'capture-receipt-attributes',
        'receipt-attribute-conflict',
        'replace-container-label',
        'variant-complete',
        'stage-asn',
        'shortage-review',
        'reprint-labels',
      ].includes(controller.state.step) ? (
        <ReceiptWorkScreen
          key={controller.state.step}
          controller={controller}
          embedded={embedded}
          onExit={exitReceiving}
        />
      ) : null}
    </ReceivingNavigationContext.Provider>
  )
}

interface MobileReceivingProps {
  embedded?: boolean
  onBack?: () => void
  onExit?: () => void
}

export const MobileReceiving = ({ embedded = false, onBack, onExit }: MobileReceivingProps = {}) => {
  const controller = useReceivingFlow()
  return <ReceivingFlowView controller={controller} embedded={embedded} onBack={onBack} onExit={onExit} />
}

export { createReceivingFlowState, getCandidateAsns, receivingFlowReducer, useReceivingFlow } from './flow'
export type {
  ReceivingFlowAction,
  ReceivingFlowController,
  ReceivingFlowSeed,
  ReceivingFlowState,
  ReceivingStep,
} from './flow'
